// src/features/statistics/components/TimelineTab.tsx
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CartesianGrid,
  LabelList,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AlertTriangle } from 'lucide-react';

import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Avatar, AvatarFallback, AvatarImage } from '../../../components/ui/avatar';
import { Skeleton } from '../../../components/ui/skeleton';
import { useAccountStore, useTikTokAccounts } from '../../../entities/account';
import { formatCompactNumber } from '../lib/format-number';
import { AccountMultiSelect } from './AccountMultiSelect';
import { useAccountTimeline } from '../hooks/useAccountTimeline';
import { readRemembered, writeRemembered } from '../../../shared/lib/remembered';

import { aggregateByMonth, yearAgoMonth, yearOverYear, type MonthPoint } from '../lib/timeline-months';

import type { DaySnapshot } from '../api/account-timeline';

const METRIC_COLORS = {
  impressions: '#2563eb',
  reach: '#dc2626',
  interactions: '#ca8a04',
  follower_delta: '#16a34a',
} as const;

const METRIC_LABELS = {
  impressions: 'Показы',
  reach: 'Охваты',
  interactions: 'Взаимодействия',
  follower_delta: 'Новые подписчики',
} as const;

type MetricKey = keyof typeof METRIC_COLORS;

const CHART_MARGIN = { top: 24, right: 16, left: 0, bottom: 0 };

const TABLE_COLUMNS: { key: keyof MonthPoint; label: string }[] = [
  { key: 'impressions', label: 'Показы' },
  { key: 'reach', label: 'Охваты' },
  { key: 'interactions', label: 'Взаимодействия' },
  { key: 'follower_delta', label: 'Новые подписчики' },
  { key: 'followers', label: 'Подписчиков на конец' },
];

function formatInt(v: number | null): string {
  return v == null ? '—' : v.toLocaleString('ru-RU');
}

function YoyBadge({ value }: { value: number | null }): JSX.Element | null {
  if (value == null || !Number.isFinite(value)) return null;
  const up = value >= 0;
  return (
    <span
      className={`block text-[11px] tabular-nums ${up ? 'text-emerald-600' : 'text-red-500'}`}
      title="К тому же месяцу прошлого года"
    >
      {up ? '+' : '−'}
      {Math.abs(value).toFixed(0)}% г/г
    </span>
  );
}

// Таблица по месяцам — новые сверху. Год к году — к тому же месяцу прошлого
// года, если он есть в истории: так сравнивают «25-й и 26-й».
function MonthTable({ points }: { points: MonthPoint[] }): JSX.Element {
  const byMonth = new Map(points.map((p) => [p.month, p]));
  const rows = [...points].reverse();
  return (
    <div className="mt-4 overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-muted-foreground border-b">
            <th className="text-left font-medium py-1.5 pr-3">Месяц</th>
            {TABLE_COLUMNS.map((c) => (
              <th key={c.key} className="text-right font-medium py-1.5 px-3 whitespace-nowrap">
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => {
            const prev = byMonth.get(yearAgoMonth(p.month));
            return (
              <tr key={p.month} className="border-b last:border-0">
                <td className="py-1.5 pr-3 whitespace-nowrap">{p.label}</td>
                {TABLE_COLUMNS.map((c) => (
                  <td key={c.key} className="py-1.5 px-3 text-right tabular-nums">
                    {formatInt(p[c.key] as number | null)}
                    <YoyBadge value={yearOverYear(p[c.key] as number | null, prev?.[c.key] as number | null)} />
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

type AccountChartProps = {
  username: string;
  avatarUrl: string | null;
  days: DaySnapshot[];
};

function AccountChart({ username, avatarUrl, days }: AccountChartProps): JSX.Element {
  const chartData = useMemo(() => aggregateByMonth(days), [days]);
  // Есть хоть одна ненулевая точка? Иначе рисовалась бы голая ось: у TikTok при
  // единственном снимке все метрики null (дельта подписчиков нужен 2-й день).
  const hasData = useMemo(
    () =>
      chartData.some(
        (p) =>
          p.impressions != null ||
          p.reach != null ||
          p.interactions != null ||
          p.follower_delta != null ||
          p.followers != null,
      ),
    [chartData],
  );
  const initials = username.slice(0, 2).toUpperCase();

  return (
    <div className="rounded-xl border bg-card p-6">
      <div className="flex items-center gap-3 mb-4">
        <Avatar className="size-8 shrink-0">
          <AvatarImage src={avatarUrl ?? undefined} alt={username} />
          <AvatarFallback className="text-xs font-semibold">{initials}</AvatarFallback>
        </Avatar>
        <span className="font-semibold text-base">@{username}</span>
      </div>

      {!hasData ? (
        <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">
          Данные ещё не накоплены. Появятся после первого запуска расписания.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={chartData} margin={CHART_MARGIN}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tickFormatter={formatCompactNumber}
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
              width={52}
            />
            <Tooltip formatter={(value: number | null) => value == null ? '—' : formatCompactNumber(value)} />
            <Legend />
            {(Object.keys(METRIC_COLORS) as MetricKey[]).map(key => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                stroke={METRIC_COLORS[key]}
                name={METRIC_LABELS[key]}
                strokeWidth={2}
                dot={{ r: 4, fill: METRIC_COLORS[key] }}
                activeDot={{ r: 6 }}
                connectNulls={false}
              >
                <LabelList
                  dataKey={key}
                  position="top"
                  formatter={formatCompactNumber}
                  style={{ fontSize: 12, fill: METRIC_COLORS[key] }}
                />
              </Line>
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}
      {hasData && <MonthTable points={chartData} />}
    </div>
  );
}

const TIMELINE_KEY = 'stats:timeline';
type TimelineSelection = { selected: string[]; known: string[] };

export function TimelineTab(): JSX.Element {
  const { accounts: igAccounts } = useAccountStore();
  const { accounts: tiktokAccounts } = useTikTokAccounts();
  // Единый список: Instagram (KV-бакеты) + TikTok (снимки account_stats_daily).
  const accounts = useMemo(() => [...igAccounts, ...tiktokAccounts], [igAccounts, tiktokAccounts]);
  // Выбор помнится между заходами: раньше каждый визит заново отмечал все
  // ~60 аккаунтов. Instagram (стор) и TikTok (Display API) грузятся в разное
  // время, поэтому авто-выбираем только ВПЕРВЫЕ увиденный аккаунт — снятые
  // пользователем (они уже в known) не возвращаем, в том числе после перезагрузки.
  const [remembered] = useState(() => readRemembered<TimelineSelection | null>(TIMELINE_KEY, null));
  const [selectedIds, setSelectedIdsState] = useState<string[]>(remembered?.selected ?? []);
  const knownRef = useRef<Set<string>>(new Set(remembered?.known ?? []));

  const setSelectedIds = (next: string[] | ((prev: string[]) => string[])) => {
    setSelectedIdsState((prev) => {
      const value = typeof next === 'function' ? next(prev) : next;
      writeRemembered<TimelineSelection>(TIMELINE_KEY, { selected: value, known: [...knownRef.current] });
      return value;
    });
  };

  useEffect(() => {
    const fresh = accounts.map(a => a.id).filter(id => !knownRef.current.has(id));
    if (fresh.length === 0) return;
    fresh.forEach(id => knownRef.current.add(id));
    setSelectedIds(prev => [...prev, ...fresh]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts]);

  // Запомненные, но с тех пор отключённые аккаунты не запрашиваем.
  const visibleIds = useMemo(
    () => selectedIds.filter(id => accounts.some(a => a.id === id)),
    [selectedIds, accounts],
  );
  const { accounts: timelineData, isLoading, error } = useAccountTimeline(visibleIds);

  return (
    <div className="flex flex-col gap-4 py-3">
      <AccountMultiSelect
        accounts={accounts}
        value={visibleIds}
        onChange={setSelectedIds}
      />

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="size-4" />
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      )}

      {isLoading && visibleIds.length > 0 && (
        <div className="flex flex-col gap-4">
          {visibleIds.map(id => (
            <Skeleton key={id} className="h-80 w-full rounded-xl" />
          ))}
        </div>
      )}

      {!isLoading && visibleIds.length === 0 && (
        <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">
          Выберите аккаунты для отображения хронологии
        </div>
      )}

      {!isLoading && !error && visibleIds.length > 0 && (
        <div className="flex flex-col gap-6">
          {visibleIds.map(accountId => {
            const entry = timelineData[accountId];
            if (!entry) return null;
            return (
              <AccountChart
                key={accountId}
                username={entry.username}
                avatarUrl={entry.avatar_url}
                days={entry.days}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
